import { useCallback, useEffect, useRef, useState } from "react";
import type { CustomerDataAdapter } from "../data/customer-adapter";

/**
 * Whether the sign-in page offers "Get access", from `GET /auth/options`. A failed read is
 * `not_offered`, not an error: the page still signs people in, it just does not promise an email.
 */
export type PortalGetAccessOptions =
  | { status: "loading" }
  | { status: "not_offered" }
  /** `message` is the backend's one answer, kept so a request that fails to reach us can still say it. */
  | { status: "offered"; message: string };

/** One Get access request, from the sign-in page's "New here" card. */
export type PortalGetAccessRequest =
  | { status: "idle" }
  | { status: "submitting" }
  /** Whatever happened that says nothing about the address: always the backend's message. */
  | { status: "sent"; message: string }
  | { status: "invalid_email"; message: string }
  | { status: "throttled"; retryAfterSeconds: number | null };

/**
 * "in about 10 minutes", for the throttled sentence. The 429's `Retry-After` is the only timing the
 * page may use; without one the answer is the honest vague one.
 */
export function describeRetryDelay(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds === null) return "in a few minutes";
  if (retryAfterSeconds <= 90) return "in a minute or so";
  const minutes = Math.ceil(retryAfterSeconds / 60);
  if (minutes < 120) return `in about ${minutes} minutes`;
  return `in about ${Math.ceil(minutes / 60)} hours`;
}

/**
 * The sign-in page's Get access flow: reads the options once, then runs requests through the
 * adapter. Headless: it decides nothing about wording. The message shown for an accepted request
 * is the backend's, and an `unavailable` result (network, 5xx, feature switched off under the page)
 * shows that same message, so a failure cannot be told apart from a stranger's request.
 */
export function usePortalGetAccess(adapter: CustomerDataAdapter): {
  options: PortalGetAccessOptions;
  request: PortalGetAccessRequest;
  submit: (email: string) => Promise<void>;
  /** Back to idle, e.g. after the person edits the address or leaves the "sent" screen. */
  reset: () => void;
} {
  const [options, setOptions] = useState<PortalGetAccessOptions>({
    status: "loading",
  });
  const [request, setRequest] = useState<PortalGetAccessRequest>({
    status: "idle",
  });
  const isMounted = useRef(true);
  const isSubmitting = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;
    adapter
      .getSignInOptions()
      .then((read) => {
        if (!isCurrent) return;
        setOptions(
          read.get_access_enabled
            ? { status: "offered", message: read.get_access_message }
            : { status: "not_offered" },
        );
      })
      .catch(() => {
        if (isCurrent) setOptions({ status: "not_offered" });
      });
    return () => {
      isCurrent = false;
    };
  }, [adapter]);

  const submit = useCallback(
    async (email: string) => {
      if (options.status !== "offered" || isSubmitting.current) return;
      isSubmitting.current = true;
      setRequest({ status: "submitting" });
      let next: PortalGetAccessRequest;
      try {
        const result = await adapter.requestPortalAccess(email.trim());
        switch (result.kind) {
          case "accepted":
            next = { status: "sent", message: result.message };
            break;
          case "invalid_email":
            next = { status: "invalid_email", message: result.message };
            break;
          case "throttled":
            next = {
              status: "throttled",
              retryAfterSeconds: result.retryAfterSeconds,
            };
            break;
          case "unavailable":
            next = { status: "sent", message: options.message };
            break;
        }
      } catch {
        // The contract says this never throws; if an adapter does, the answer is still the same one.
        next = { status: "sent", message: options.message };
      } finally {
        isSubmitting.current = false;
      }
      if (isMounted.current) setRequest(next);
    },
    [adapter, options],
  );

  const reset = useCallback(
    () =>
      setRequest((current) =>
        current.status === "submitting" ? current : { status: "idle" },
      ),
    [],
  );

  return { options, request, submit, reset };
}
